import { useState, useRef } from 'react';
import { X, User, KeyRound, FileText, Pill, StickyNote, Save, Camera, Phone, Users, Brain, Heart, Trash2, Plus, Clock, AlertCircle } from 'lucide-react';
import { MED_TIMES } from '../lib/constants';

const SECTIONS = [
  { id: 'basic', label: 'أساسي', icon: User },
  { id: 'contact', label: 'التواصل', icon: Phone },
  { id: 'clinical', label: 'طبي', icon: Brain },
  { id: 'patterns', label: 'أنماط', icon: Heart },
  { id: 'notes', label: 'ملاحظات', icon: StickyNote }
];

export default function AddPatientModal({ existing, onClose, onSave }) {
  const isEdit = !!existing;
  const [section, setSection] = useState('basic');
  const fileRef = useRef();

  // Basic
  const [name, setName] = useState(existing?.name || '');
  const [pin, setPin] = useState(existing?.pin || '');
  const [photo, setPhoto] = useState(existing?.photo || null);
  const [age, setAge] = useState(existing?.age || '');
  const [gender, setGender] = useState(existing?.gender || 'أنثى');
  const [occupation, setOccupation] = useState(existing?.occupation || '');

  // Contact
  const [phone, setPhone] = useState(existing?.phone || '');
  const [address, setAddress] = useState(existing?.address || '');
  const [emergencyName, setEmergencyName] = useState(existing?.emergency_name || '');
  const [emergencyPhone, setEmergencyPhone] = useState(existing?.emergency_phone || '');
  const [emergencyRelation, setEmergencyRelation] = useState(existing?.emergency_relation || '');

  // Clinical
  const [diagnosis, setDiagnosis] = useState(existing?.diagnosis || '');
  const [previousDiagnoses, setPreviousDiagnoses] = useState(existing?.previous_diagnoses || '');
  const [therapyStartDate, setTherapyStartDate] = useState(existing?.therapy_start_date || '');
  const [hospitalizations, setHospitalizations] = useState(existing?.hospitalizations || '');
  const [medications, setMedications] = useState(existing?.medications?.map(m => typeof m === 'string' ? { name: m, schedule: [] } : m) || []);

  // Patterns
  const [sleepPattern, setSleepPattern] = useState(existing?.sleep_pattern || '');
  const [energyPattern, setEnergyPattern] = useState(existing?.energy_pattern || '');
  const [triggers, setTriggers] = useState(existing?.triggers || '');
  const [copingMechanisms, setCopingMechanisms] = useState(existing?.coping_mechanisms || '');
  const [supportNetwork, setSupportNetwork] = useState(existing?.support_network || '');

  // Notes
  const [notes, setNotes] = useState(existing?.notes || '');
  const [personalityNotes, setPersonalityNotes] = useState(existing?.personality_notes || '');
  const [thoughtsPatterns, setThoughtsPatterns] = useState(existing?.thoughts_patterns || '');

  const [err, setErr] = useState('');

  const handlePhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 500000) { setErr('حجم الصورة يجب أن يكون أقل من 500KB'); return; }
    const reader = new FileReader();
    reader.onload = e => setPhoto(e.target.result);
    reader.readAsDataURL(f);
  };

  const addMedication = () => setMedications([...medications, { name: '', schedule: ['morning'] }]);
  const updateMed = (i, field, value) => { const n = [...medications]; n[i][field] = value; setMedications(n); };
  const toggleMedTime = (i, timeCode) => { const n = [...medications]; const arr = n[i].schedule || []; if (arr.includes(timeCode)) n[i].schedule = arr.filter(x => x !== timeCode); else n[i].schedule = [...arr, timeCode]; setMedications(n); };
  const removeMed = (i) => setMedications(medications.filter((_, idx) => idx !== i));

  const handleSave = () => {
    if (!name.trim() || !pin.trim()) { setErr('الاسم والرقم السري مطلوبان'); setSection('basic'); return; }
    const cleanMeds = medications.filter(m => m.name.trim()).map(m => ({ name: m.name.trim(), schedule: m.schedule || [] }));
    onSave({
      name: name.trim(), pin: pin.trim(), photo,
      age: age ? +age : null, gender, occupation: occupation.trim(),
      phone: phone.trim(), address: address.trim(),
      emergency_name: emergencyName.trim(), emergency_phone: emergencyPhone.trim(), emergency_relation: emergencyRelation.trim(),
      diagnosis: diagnosis.trim() || 'لم يحدد',
      previous_diagnoses: previousDiagnoses.trim(),
      therapy_start_date: therapyStartDate,
      hospitalizations: hospitalizations.trim(),
      medications: cleanMeds,
      sleep_pattern: sleepPattern.trim(), energy_pattern: energyPattern.trim(),
      triggers: triggers.trim(), coping_mechanisms: copingMechanisms.trim(), support_network: supportNetwork.trim(),
      notes: notes.trim(), personality_notes: personalityNotes.trim(), thoughts_patterns: thoughtsPatterns.trim()
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden animate-slide-up flex flex-col shadow-2xl" style={{ boxShadow: '0 30px 90px rgba(139,127,240,0.25)' }}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-[var(--border-soft)] flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #8b7ff0, #5eb2a3)' }}>
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-[16px] font-extrabold" style={{ color: 'var(--text)' }}>{isEdit ? 'تعديل ملف المريضة' : 'إضافة مريضة جديدة'}</h3>
            <p className="text-[11px]" style={{ color: 'var(--text-2)' }}>ملف شامل يساعد الأطباء الذكيين في دعمها</p>
          </div>
          <button onClick={onClose} className="mr-auto w-9 h-9 rounded-xl bg-[var(--surface-2)] hover:bg-[var(--border-soft)] flex items-center justify-center transition-colors">
            <X className="w-4 h-4" style={{ color: 'var(--text-2)' }} />
          </button>
        </div>

        {/* Section tabs */}
        <div className="px-5 py-3 flex gap-1.5 overflow-x-auto border-b border-[var(--border-soft)]">
          {SECTIONS.map(s => {
            const Icon = s.icon;
            const isActive = section === s.id;
            return (
              <button key={s.id} onClick={() => setSection(s.id)} className={`flex items-center gap-1.5 rounded-2xl px-3 py-2 text-[12px] font-bold whitespace-nowrap flex-shrink-0 transition-all border ${isActive ? 'shadow-sm' : ''}`}
                style={isActive ? { background: 'var(--primary-bg)', borderColor: 'var(--primary)', color: 'var(--primary-dark)' } : { background: 'transparent', borderColor: 'var(--border-soft)', color: 'var(--text-2)' }}>
                <Icon className="w-3.5 h-3.5" />
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Basic */}
          {section === 'basic' && (
            <div className="space-y-4">
              {/* Photo upload */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  {photo ? (
                    <img src={photo} alt="" className="w-20 h-20 rounded-2xl object-cover border-2 border-[var(--border)]" />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: 'var(--surface-2)', border: '2px dashed var(--border)' }}>
                      <User className="w-8 h-8" style={{ color: 'var(--text-3)' }} />
                    </div>
                  )}
                  <button onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -left-1 w-7 h-7 rounded-full bg-white border border-[var(--border)] shadow-md flex items-center justify-center hover:scale-110 transition-transform">
                    <Camera className="w-3.5 h-3.5" style={{ color: 'var(--primary-dark)' }} />
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
                </div>
                <div>
                  <div className="text-[13px] font-extrabold" style={{ color: 'var(--text)' }}>الصورة الشخصية</div>
                  <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>يساعد في تعزيز الألفة</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="الاسم *" value={name} onChange={setName} placeholder="سارة" />
                <Field label="الرقم السري *" value={pin} onChange={setPin} placeholder="1234" inputMode="numeric" />
                <Field label="العمر" value={age} onChange={setAge} placeholder="28" inputMode="numeric" />
                <div>
                  <label className="text-[11px] font-bold block mb-1.5" style={{ color: 'var(--text-2)' }}>الجنس</label>
                  <select value={gender} onChange={e => setGender(e.target.value)} className="w-full bg-[var(--surface-2)] border border-[var(--border-soft)] focus:border-[var(--primary)] rounded-xl px-3 py-2.5 text-[13px] outline-none transition-colors" style={{ color: 'var(--text)' }}>
                    <option>أنثى</option>
                    <option>ذكر</option>
                  </select>
                </div>
              </div>
              <Field label="المهنة / الدراسة" value={occupation} onChange={setOccupation} placeholder="موظفة، طالبة، ربة منزل…" />
            </div>
          )}

          {/* Contact */}
          {section === 'contact' && (
            <div className="space-y-4">
              <Field label="رقم الجوال" value={phone} onChange={setPhone} placeholder="05xxxxxxxx" inputMode="tel" icon={Phone} />
              <Field label="العنوان" value={address} onChange={setAddress} placeholder="المدينة، الحي" />
              <div className="pt-3 border-t border-[var(--border-soft)]">
                <div className="text-[11px] font-extrabold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: 'var(--text-2)' }}>
                  <AlertCircle className="w-3.5 h-3.5" />
                  جهة اتصال في حالة الطوارئ
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="الاسم" value={emergencyName} onChange={setEmergencyName} placeholder="أحمد - الأخ" />
                  <Field label="العلاقة" value={emergencyRelation} onChange={setEmergencyRelation} placeholder="أخ، أم، زوج" />
                </div>
                <div className="mt-3">
                  <Field label="رقم الجوال" value={emergencyPhone} onChange={setEmergencyPhone} placeholder="05xxxxxxxx" inputMode="tel" />
                </div>
              </div>
            </div>
          )}

          {/* Clinical */}
          {section === 'clinical' && (
            <div className="space-y-4">
              <Field label="التشخيص الحالي" value={diagnosis} onChange={setDiagnosis} placeholder="ثنائي القطب النوع الأول" />
              <TextArea label="تشخيصات سابقة" value={previousDiagnoses} onChange={setPreviousDiagnoses} placeholder="اكتبي أي تشخيصات تم وضعها سابقاً…" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="تاريخ بدء العلاج" value={therapyStartDate} onChange={setTherapyStartDate} placeholder="2023-06" />
              </div>
              <TextArea label="دخول مستشفى (إن وجد)" value={hospitalizations} onChange={setHospitalizations} placeholder="التاريخ، السبب، المدة…" />
              {/* Medications */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-bold flex items-center gap-1.5" style={{ color: 'var(--text-2)' }}>
                    <Pill className="w-3.5 h-3.5" />
                    الأدوية
                  </label>
                  <button onClick={addMedication} className="rounded-lg px-2.5 py-1 text-[11px] font-bold flex items-center gap-1" style={{ background: 'var(--secondary-bg)', color: 'var(--secondary-dark)', border: '1px solid var(--secondary-light)' }}>
                    <Plus className="w-3 h-3" /> إضافة
                  </button>
                </div>
                <div className="space-y-2">
                  {medications.map((m, i) => (
                    <div key={i} className="rounded-2xl p-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--border-soft)' }}>
                      <div className="flex gap-2 mb-2">
                        <input value={m.name} onChange={e => updateMed(i, 'name', e.target.value)} placeholder="ليثيوم 400mg" className="flex-1 bg-white border border-[var(--border-soft)] focus:border-[var(--primary)] rounded-xl px-3 py-2 text-[13px] outline-none" style={{ color: 'var(--text)' }} />
                        <button onClick={() => removeMed(i)} className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="text-[10px] font-semibold mb-1.5" style={{ color: 'var(--text-2)' }}>مواعيد الأخذ:</div>
                      <div className="flex flex-wrap gap-1.5">
                        {MED_TIMES.map(t => {
                          const sel = (m.schedule || []).includes(t.code);
                          return (
                            <button key={t.code} onClick={() => toggleMedTime(i, t.code)} className={`rounded-lg px-2 py-1 text-[10.5px] font-bold flex items-center gap-1 border transition-all ${sel ? 'shadow-sm' : ''}`}
                              style={sel ? { background: `${t.color}22`, borderColor: t.color, color: t.color } : { background: 'white', borderColor: 'var(--border-soft)', color: 'var(--text-2)' }}>
                              <Clock className="w-3 h-3" /> {t.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {medications.length === 0 && (
                    <div className="text-center text-[12px] py-4" style={{ color: 'var(--text-3)' }}>لا توجد أدوية — أضيفي أدوية المريضة</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Patterns */}
          {section === 'patterns' && (
            <div className="space-y-4">
              <TextArea label="نمط النوم" value={sleepPattern} onChange={setSleepPattern} placeholder="متى تنام، جودة النوم، متى تستيقظ، أي أرق…" />
              <TextArea label="مستوى الطاقة" value={energyPattern} onChange={setEnergyPattern} placeholder="متي تنخفض، متى ترتفع، أوقات الخمود…" />
              <TextArea label="المحفزات (Triggers)" value={triggers} onChange={setTriggers} placeholder="مواقف، أشخاص، أحداث، مواسم…" />
              <TextArea label="آليات التأقلم" value={copingMechanisms} onChange={setCopingMechanisms} placeholder="ما تفعله للتهدئة، للتركيز، للنوم…" />
              <TextArea label="شبكة الدعم" value={supportNetwork} onChange={setSupportNetwork} placeholder="العائلة، الأصدقاء، الطبيب المعالج…" />
            </div>
          )}

          {/* Notes */}
          {section === 'notes' && (
            <div className="space-y-4">
              <TextArea label="ملاحظات عامة" value={notes} onChange={setNotes} placeholder="أي ملاحظات مهمة…" rows={3} />
              <TextArea label="الشخصية والمزاج العام" value={personalityNotes} onChange={setPersonalityNotes} placeholder="منفتحة، حساسة، تحليلية، إبداعية…" rows={3} />
              <TextArea label="أنماط التفكير" value={thoughtsPatterns} onChange={setThoughtsPatterns} placeholder="التفكير المفرط، تفكير أبيض وأسود، اجترار الأفكار…" rows={3} />
            </div>
          )}

          {err && (
            <div className="mt-4 text-[12px] font-semibold rounded-xl px-3 py-2" style={{ background: 'var(--danger-bg)', color: '#a35555', border: '1px solid rgba(229,133,133,0.3)' }}>
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-[var(--border-soft)] flex gap-2 bg-[var(--surface-2)]">
          <button onClick={onClose} className="flex-1 bg-white border border-[var(--border)] hover:bg-[var(--surface-3)] rounded-2xl py-3 text-[13.5px] font-bold transition-colors" style={{ color: 'var(--text-2)' }}>
            إلغاء
          </button>
          <button onClick={handleSave} className="flex-1 rounded-2xl py-3 text-white text-[13.5px] font-extrabold flex items-center justify-center gap-1.5 shadow-lg hover:opacity-95 active:scale-[0.98] transition-all" style={{ background: 'linear-gradient(135deg, #8b7ff0, #6a5cd8)' }}>
            <Save className="w-4 h-4" />
            {isEdit ? 'حفظ التعديلات' : 'حفظ المريضة'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, inputMode, icon: Icon }) {
  return (
    <div>
      <label className="text-[11px] font-bold block mb-1.5" style={{ color: 'var(--text-2)' }}>{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-3)' }} />}
        <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} inputMode={inputMode}
          className={`w-full bg-[var(--surface-2)] border border-[var(--border-soft)] focus:border-[var(--primary)] rounded-xl px-3 py-2.5 text-[13px] outline-none transition-colors ${Icon ? 'pr-9' : ''}`} style={{ color: 'var(--text)' }} />
      </div>
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder, rows = 2 }) {
  return (
    <div>
      <label className="text-[11px] font-bold block mb-1.5" style={{ color: 'var(--text-2)' }}>{label}</label>
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        className="w-full bg-[var(--surface-2)] border border-[var(--border-soft)] focus:border-[var(--primary)] rounded-xl px-3 py-2.5 text-[13px] outline-none transition-colors resize-none leading-relaxed" style={{ color: 'var(--text)' }} />
    </div>
  );
}
